import { FC, useEffect, useState } from 'react';
import { Flex, Text, Button } from '@chakra-ui/react';
import { Loader, Button as PinkButton } from 'components';
import qs from 'qs';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { signInInvite, logout } from 'modules/auth/authSlice';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import { getUserInfo, PORTAL_CONTEXT_KEY } from 'modules/user/userSlice';
import { rem } from 'utils/fonts';
import createUserApi, { getInvitedUser } from 'api/user';
import { appContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';

const Wrapper = styled(Flex)`
  height: calc(100vh - 90px);
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

interface InviteData {
  invite: string;
  code: string;
  customerId?: string;
  context?: string;
}

const Invite: FC = () => {
  const { search } = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const data = (qs.parse(search, { ignoreQueryPrefix: true }) as unknown) as InviteData;
  const dispatch = useDispatch();
  const [valid, setValid] = useState(false);
  const [error, setError] = useState<string>();
  const [customerCompany, setCustomerCompany] = useState('');
  const history = useHistory();
  const { t } = useTranslation('signup');
  const { accounts } = useSelector((state: RootState) => state.user);
  const context = useSelector(appContextSelector);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const hasAccounts = accounts && accounts.length > 0;

  async function verifyInvitation(): Promise<void> {
    try {
      const invitation = await getInvitedUser(data.invite);
      setCustomerCompany(invitation?.customer?.name ?? '');
      if (invitation.claimed) {
        setError(t('invite.invitationUsed'));
        return;
      }
      setValid(true);
    } catch {
      setError(t('invite.inviteError'));
    }
  }

  async function completeInvitation(): Promise<void> {
    try {
      const currentContext = (data.context ?? context) as PortalContext;
      const userApi = createUserApi(currentContext);
      await userApi.completeInvitation(data.invite, data.code, data.customerId);
      if (data.context) {
        localStorage.setItem(PORTAL_CONTEXT_KEY, data.context);
      }
      dispatch(getUserInfo(true));
      history.replace('/?acceptpolicy=true');
    } catch {
      setError(t('invite.inviteError'));
    }
  }

  function handleGoToLogin(): void {
    dispatch(logout(`${window.origin}`));
  }

  useEffect(() => {
    if (hasAccounts) {
      return;
    }
    verifyInvitation();
  }, []);

  useEffect(() => {
    if (hasAccounts) {
      return;
    }
    if (isAuthenticated && valid) {
      completeInvitation();
    } else if (!isAuthenticated && valid) {
      dispatch(signInInvite(search, customerCompany));
    }
  }, [isAuthenticated, valid]);

  if (hasAccounts) {
    return <Redirect to="/" />;
  }

  const GoBackButton = isCorporateTheme ? Button : PinkButton;

  return (
    <Wrapper>
      {error ? (
        <>
          <Text fontSize={rem(18)} color="other.CTA" fontWeight="700">
            {error}
          </Text>
          <GoBackButton mt="20px" onClick={handleGoToLogin} minW="100px">
            {t('invite.goToLoginButton')}
          </GoBackButton>
        </>
      ) : (
        <Loader />
      )}
    </Wrapper>
  );
};

export default Invite;
